// src/contexts/AuthContext.js
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

/**
 * SSOT for Firebase authentication state.
 * Maintains single onAuthStateChanged listener to prevent race conditions.
 * @typedef {'loading'|'signedOut'|'signedIn'|'error'} AuthStatus
 */
const AuthContext = createContext(undefined);

const initialState = {
  status: 'loading',
  user: null,
  uid: null,
  error: null,
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(() => ({ ...initialState }));

  useEffect(() => {
    // Prevents state updates after unmount (React 18 Strict Mode guard)
    let active = true;

    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        if (!active) return; // Skip if component unmounted

        if (firebaseUser) {
          setState({
            status: 'signedIn',
            user: firebaseUser,
            uid: firebaseUser.uid,
            error: null,
          });
        } else {
          setState({
            status: 'signedOut',
            user: null,
            uid: null,
            error: null,
          });
        }
      },
      (error) => {
        if (!active) return;
        console.error('Auth state subscription error:', error);
        setState({
          status: 'error',
          user: null,
          uid: null,
          error,
        });
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const value = useMemo(() => state, [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access authentication state.
 * @returns {Object} Auth state with status, user, uid, and error
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
